// @owners { team: patients-team }
import { ActionSheetContext, Toast, ToastContext } from '@alto/design-system';
import { type CheckoutFlowType } from '@alto/scriptdash/alto/patient_app/checkout_flow/types/v1/checkout_flow_type';
import { type AddToCartMetadata } from '@alto/scriptdash/alto/patient_app/checkout_flow/v1/checkout_flow_endpoint';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchNextAvailableDates } from '~shared/actions/nextAvailableDates';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { getDefaultAddress } from '~shared/features/checkout/selectors/getDefaultAddress';
import { partitionMedicationsByActiveState } from '~shared/features/my-meds/selectors/partitionMedicationsByActiveState';
import { getErrorMessage } from '~shared/helpers/helper';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { Sentry } from '~shared/sentry';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type APIError } from '~shared/types';
import { AncillaryItemsActionSheet } from '../../ancillaries/components/AncillaryItemsActionSheet';
import { OrderTypeActionSheet } from '../../auto-refill/components/order-type-action-sheet/OrderTypeActionSheet';
import { ORDER_TYPE_CONTEXT } from '../../auto-refill/constants';
import { CompoundedMedicationActionSheet } from '../../med-details/components/CompoundedMedicationActionSheet';
import { MissingAddressActionSheet } from '../components/MissingAddressActionSheet';
import { SelectPrescriptionActionSheet } from '../components/SelectPrescriptionActionSheet';
import { addToCartQueryKeys, fetchAddToCart } from '../queries/queries';
import { type AddToCartParams, useAddToCart } from './useAddToCart';

type QueryData = {
  actionSheetFlowData: AddToCartMetadata | null | undefined;
  actionSheetFlowTypes: CheckoutFlowType[];
};

/** Per-Rx checkout can still list `multiple_active_rx` after a pick; that step is already done on the sheet, so skip it. */
const indexAfterMultipleActiveRxSelection = (flowTypes: CheckoutFlowType[]): number => {
  let i = 0;
  while (i < flowTypes.length && flowTypes[i] === 'multiple_active_rx') {
    i += 1;
  }
  return i;
};

type AddToCartOrigin = typeof ORIGIN_NAMES.HOMESCREEN | typeof ORIGIN_NAMES.MED_LIST | typeof ORIGIN_NAMES.MED_DETAILS;

export type UseAddToCartFlowParams = {
  origin: AddToCartOrigin;
};

/**
 * Single-instance hook that imperatively drives the add-to-cart flow including all
 * interstitial action sheets. Mount one instance per feature area (list or screen),
 * not per row, to avoid N×M render-phase subscriptions.
 *
 * Call `handleAddToCart(prescriptionID)` from any row's button handler.
 */
export const useAddToCartFlow = ({ origin }: UseAddToCartFlowParams) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const { addToast } = useContext(ToastContext);
  const address = useSelectorShared(getDefaultAddress);
  const addressID = address?.id;
  const { active: activeMeds } = useSelectorShared(partitionMedicationsByActiveState);
  const { prescriptionIDs: cartPrescriptionIDs, isPrescriptionInCart } = usePrescriptionsInCart();
  const { handleAddToCart: addToCart, isPending: isLoading } = useAddToCart();

  const handleAddToCart = useCallback(
    async (
      prescriptionID: number,
      { selectedAutoRefillStatus }: { selectedAutoRefillStatus?: boolean | null } = {},
    ) => {
      if (!addressID) {
        setActiveActionSheet(<MissingAddressActionSheet origin={origin} />);
        return;
      }

      try {
        await dispatch(
          // eslint-disable-next-line import/no-deprecated
          fetchNextAvailableDates({
            unbundledPrescriptionIDs: [prescriptionID],
            addressID,
          }),
        );

        const queryKey = addToCartQueryKeys.prescription({ addressID, prescriptionID, cartPrescriptionIDs });
        const cachedData = queryClient.getQueryData<QueryData>(queryKey);
        let data: QueryData;

        if (cachedData) {
          data = cachedData;
        } else {
          data = await queryClient.fetchQuery({ queryKey, queryFn: fetchAddToCart });
        }

        // Prefetch must run even when `data` came from cache; otherwise choosing another Rx in
        // SelectPrescriptionActionSheet leaves no per-rx checkout_flow_types in the query cache.
        if (data.actionSheetFlowData?.multiple_active_rx?.prescriptions) {
          const uncached = data.actionSheetFlowData.multiple_active_rx.prescriptions.filter(({ id }) => {
            if (id === prescriptionID) return false;
            return !queryClient.getQueryData(
              addToCartQueryKeys.prescription({ addressID, prescriptionID: id, cartPrescriptionIDs }),
            );
          });
          await Promise.allSettled(
            uncached.map(({ id }) =>
              queryClient.prefetchQuery({
                queryKey: addToCartQueryKeys.prescription({ addressID, prescriptionID: id, cartPrescriptionIDs }),
                queryFn: fetchAddToCart,
              }),
            ),
          );
        }

        const baseFlowTypes = data.actionSheetFlowTypes;
        // do not show the multiple_active_rx sheet if the origin is med details because the patient
        // can select the prescription they want to add to the cart
        const flowTypesToShow =
          origin === ORIGIN_NAMES.MED_DETAILS ? baseFlowTypes.filter((t) => t !== 'multiple_active_rx') : baseFlowTypes;
        const flowDataToShow = data.actionSheetFlowData;

        const showNextSheet = ({
          flowTypes,
          flowData,
          prescriptionID: rxID,
          index,
          hasAddedToCart,
        }: {
          flowTypes: CheckoutFlowType[];
          flowData: AddToCartMetadata | null | undefined;
          prescriptionID: number;
          index: number;
          hasAddedToCart: boolean;
        }) => {
          const goNext = (overrides: { hasAddedToCart?: boolean } = {}) => {
            showNextSheet({
              flowTypes,
              flowData,
              prescriptionID: rxID,
              index: index + 1,
              hasAddedToCart: overrides.hasAddedToCart ?? hasAddedToCart,
            });
          };
          const goBack = () => {
            if (index === 0) {
              closeActionSheet();
              return;
            }
            showNextSheet({
              flowTypes,
              flowData,
              prescriptionID: rxID,
              index: index - 1,
              hasAddedToCart,
            });
          };
          const commitToCart = () => {
            const params: AddToCartParams = {
              resource_id: rxID,
              resource_type: 'Prescription',
              auto_refill_enabled: selectedAutoRefillStatus,
            };
            void addToCart(params).catch((err: unknown) => Sentry.captureException(err));
          };

          // ancillary and compounded share the same auto-commit + back/next + (med details skip) logic.
          // Extracted as an inner function so its complexity is bucketed separately from showNextSheet.
          const handleInterstitialBranch = (sheetName: 'ancillary' | 'compounded') => {
            const hasAutoRefillAfter = flowTypes.slice(index + 1).includes('auto_refill_eligible');
            let nextHasAddedToCart = hasAddedToCart;

            if (!hasAutoRefillAfter && !hasAddedToCart && !isPrescriptionInCart(rxID)) {
              commitToCart();
              nextHasAddedToCart = true;
            }

            // if origin is med details, don't show ancillary items or compound medication action sheet
            if (origin === ORIGIN_NAMES.MED_DETAILS) {
              goNext({ hasAddedToCart: nextHasAddedToCart });
              return;
            }

            const handlePress = () => {
              if (nextHasAddedToCart) closeActionSheet();
              goNext({ hasAddedToCart: nextHasAddedToCart });
            };

            if (sheetName === 'ancillary') {
              setActiveActionSheet(
                <AncillaryItemsActionSheet
                  prescriptionID={rxID}
                  onBack={goBack}
                  onPress={handlePress}
                />,
              );
              return;
            }

            const medication = activeMeds.find((m) => m.prescriptions.some((p) => p.id === rxID));
            const medicationName = medication?.medicationName ?? 'The medication';
            setActiveActionSheet(
              <CompoundedMedicationActionSheet
                medicationName={medicationName}
                onBack={goBack}
                onPress={handlePress}
              />,
            );
          };

          if (index >= flowTypes.length) {
            if (!hasAddedToCart && !isPrescriptionInCart(rxID)) {
              commitToCart();
            }
            closeActionSheet();
            return;
          }

          const sheetName = flowTypes[index];

          if (sheetName === 'multiple_active_rx') {
            const medication = activeMeds.find((m) => m.prescriptions.some((p) => p.id === rxID));
            if (!medication) {
              goNext();
              return;
            }

            const prescriptions = flowData?.multiple_active_rx?.prescriptions ?? [];
            const hasSheetsLeft = flowTypes.length - index > 1;
            setActiveActionSheet(
              <SelectPrescriptionActionSheet
                hasSheetsLeft={hasSheetsLeft}
                prescriptions={prescriptions}
                onPrescriptionSelect={(p) => {
                  const selectedQueryKey = addToCartQueryKeys.prescription({
                    addressID,
                    prescriptionID: p.id,
                    cartPrescriptionIDs,
                  });
                  const fromCache = queryClient.getQueryData<QueryData>(selectedQueryKey);
                  if (!fromCache) {
                    const err = new Error(
                      'Add to cart: checkout flow missing from cache after selecting a prescription (prefetch may have failed)',
                    );
                    Sentry.captureException(err, {
                      extra: { prescriptionId: p.id, addressId: addressID },
                    });
                    addToast(
                      <Toast
                        variant="error"
                        duration={3000}
                      >
                        There was an error loading your prescription. Please try again.
                      </Toast>,
                    );
                    return;
                  }
                  showNextSheet({
                    flowTypes: fromCache.actionSheetFlowTypes,
                    flowData: fromCache.actionSheetFlowData,
                    prescriptionID: p.id,
                    index: indexAfterMultipleActiveRxSelection(fromCache.actionSheetFlowTypes),
                    hasAddedToCart: false,
                  });
                }}
              />,
            );
            return;
          }

          if (sheetName === 'ancillary' || sheetName === 'compounded') {
            handleInterstitialBranch(sheetName);
            return;
          }

          // auto_refill_eligible is always terminal — the sheet handles add to cart internally
          if (sheetName === 'auto_refill_eligible') {
            setActiveActionSheet(
              <OrderTypeActionSheet
                prescriptionID={rxID}
                initialAutoRefillEnabledValue={selectedAutoRefillStatus}
                orderTypeContext={ORDER_TYPE_CONTEXT.ADD_TO_CART}
                handleBack={goBack}
              />,
            );
          }
        };

        // start the flow with the first action sheet to show
        showNextSheet({
          flowTypes: flowTypesToShow,
          flowData: flowDataToShow,
          prescriptionID,
          index: 0,
          hasAddedToCart: false,
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err as APIError);
        Sentry.captureMessage('Error adding to cart', {
          contexts: { 'Add to cart error': { error: errorMessage } },
          level: 'warning',
        });
        addToast(
          <Toast
            variant="error"
            duration={3000}
          >
            {errorMessage}
          </Toast>,
        );
      }
    },
    [
      activeMeds,
      addToCart,
      addToast,
      addressID,
      cartPrescriptionIDs,
      closeActionSheet,
      dispatch,
      isPrescriptionInCart,
      origin,
      queryClient,
      setActiveActionSheet,
    ],
  );

  return {
    handleAddToCart,
    isLoading,
  };
};
